import { selectAccessibleFleetIds } from 'events/accessibleFleets';
import { ALL_FLEETS } from 'events/channels';
import { realtimeEventsApi } from 'services/realtimeEvents';
import { systemFleetsApi } from 'services/systemAdmin/fleets';
import type { RootDispatch } from 'store/setup';

type ClaimsState = Parameters<typeof selectAccessibleFleetIds>[0];
type Stream = { unsubscribe: () => void };

const LIST_FLEETS_ARG = 'ListSystemFleets';

const openStreams = new Map<string, Stream[]>();
let generation = 0;

const open = (dispatch: RootDispatch, fleetId: string): Stream[] => [
    dispatch(realtimeEventsApi.endpoints.onVehicleTelemetrySubscription.initiate(fleetId)),
    dispatch(realtimeEventsApi.endpoints.onMissionStateSubscription.initiate(fleetId)),
];

const close = (fleetId: string) => {
    for (const stream of openStreams.get(fleetId) ?? []) {
        stream.unsubscribe();
    }
    openStreams.delete(fleetId);
};

/** Falls back to `ALL_FLEETS`, and so to one namespace-wide channel, if the fleet list fails. */
const resolveFleetIds = async (dispatch: RootDispatch, state: ClaimsState) => {
    const claimed = selectAccessibleFleetIds(state);

    if (!claimed.includes(ALL_FLEETS)) {
        return claimed;
    }

    const request = dispatch(systemFleetsApi.endpoints.listSystemFleets.initiate(LIST_FLEETS_ARG));
    try {
        const fleets = await request.unwrap();
        return [...new Set(fleets.map(fleet => fleet.id))].sort();
    } catch (error) {
        console.warn('[events] fleet list unavailable, subscribing to the whole namespace instead', error);
        return claimed;
    } finally {
        request.unsubscribe();
    }
};

/** Unsubscribing releases the RTK Query cache entry, which closes the channel. */
export const syncFleetStreams = async (dispatch: RootDispatch, state: ClaimsState) => {
    const sync = ++generation;
    const wanted = new Set(await resolveFleetIds(dispatch, state));

    // Resolving the fleet list yields, so a sign-out or a newer sync may have overtaken this one.
    if (sync !== generation) {
        return;
    }

    for (const fleetId of [...openStreams.keys()]) {
        if (!wanted.has(fleetId)) {
            close(fleetId);
        }
    }

    for (const fleetId of wanted) {
        if (!openStreams.has(fleetId)) {
            openStreams.set(fleetId, open(dispatch, fleetId));
        }
    }
};

export const closeAllFleetStreams = () => {
    generation += 1;
    for (const fleetId of [...openStreams.keys()]) {
        close(fleetId);
    }
};
